// The one encoder and one decoder for a stored money column on a realtime or
// catch-up payload (ADR 060 — Money Type § 2.2–2.3).
//
// Every payload that carries a row's money — both bid ledgers and the bidder's
// REST bid history — encodes with `encodeRowMoney` and decodes with `decodeWireAmount`, so a live row and a
// catch-up row carry identical bytes (ADR 011 — Durable Events) and there is
// one decode rule to change, not one per payload (ADR 044 — Must Consolidate).

import type Decimal from 'decimal.js';

import { fromWire, toWire, type MoneyWire } from '../../isomorphic/money/codec';
import { isSupportedCurrency } from '../../isomorphic/money/currency';
import { isTypedDecimal } from '../../isomorphic/money/parse-money-input';
import type { Money } from '../../isomorphic/money/money';
import { storedMoney } from '../../isomorphic/money/stored-money';

/**
 * Encode a stored amount and its row's currency for the wire:
 * `{ amount: '300.0000', currency: 'USD' }`.
 *
 * Throws on a currency outside the supported set rather than dropping the row:
 * a dropped ledger row gaps `lot_seq` and the subscriber chases it with a
 * refetch that can never fill it. The throw lands in the publisher's per-row
 * catch (logged) or the catch-up route's `apiHandler` (500), both loud. The DB
 * CHECKs only enforce `^[A-Z]{3}$`, so the branch is reachable.
 *
 * @param row - The amount — a `Decimal` from a `moneyNumeric` column, or the
 *   exact decimal string a raw SQL read returns (the admin lot-state row's
 *   `soldHammerAmountSql` hammer; BL-2009 — its `start_price` / `reserve` are
 *   Decimals since BL-2239) — and
 *   the row's own ISO 4217 currency.
 * @returns The `MoneyWire`.
 * @throws TypeError when the currency is not supported (from `storedMoney`).
 */
export function encodeRowMoney(row: { amount: Decimal | string; currency: string }): MoneyWire {
    // BL-2147: the row → Money step has one owner; this encoder composes it.
    return toWire(storedMoney(row.amount, row.currency));
}

/**
 * Encode a lot's per-lot increment override, folding the "no override" sentinel
 * — a zero or negative amount, which the admin console writes to clear an
 * override — into `null`. One place, server-side, so every client reads `null`
 * as "use the tier table" with no rule of its own (BL-2021 D2). Shared by the
 * bidder `lot_snapshot` mapper and the admin bid-increments read (BL-2104).
 *
 * @param increment - The stored override (a `Decimal` or the `numeric` driver string), or null.
 * @param currency - The lot row's own currency.
 * @returns The override as MoneyWire, or null for none.
 * @throws TypeError when the currency is not supported (see {@link encodeRowMoney}).
 */
export function encodeIncrementOverride(increment: Decimal | string | null, currency: string): MoneyWire | null {
    if (increment == null) return null;
    const wire = encodeRowMoney({ amount: increment, currency });
    return wire.amount.startsWith('-') || /^0(\.0+)?$/.test(wire.amount) ? null : wire;
}

/** How one wire amount decoded. Callers render `money`, or log and skip. */
export type WireAmountDecode =
    | { kind: 'money'; money: Money }
    /** Not a well-formed MoneyWire in a supported currency — logged and
     *  skipped by the caller, never thrown. */
    | { kind: 'undecodable' };


/** A well-formed `{ amount, currency }` in a supported currency. */
function isMoneyWire(value: unknown): value is MoneyWire {
    if (typeof value !== 'object' || value === null) return false;
    const { amount, currency } = value as { amount?: unknown; currency?: unknown };
    return typeof amount === 'string' && isTypedDecimal(amount) && isSupportedCurrency(currency);
}

/**
 * Decode one amount off the wire. The MoneyWire is the only shape a server
 * emits; anything else is reported `undecodable` so the client can log it and
 * leave the row out rather than crash the page. Pure: the caller decides what
 * to log.
 *
 * @param amount - The raw amount as it arrived (broadcast or catch-up).
 * @returns The decoded `Money`, or `undecodable`.
 */
export function decodeWireAmount(amount: unknown): WireAmountDecode {
    if (!isMoneyWire(amount)) return { kind: 'undecodable' };
    return { kind: 'money', money: fromWire(amount) };
}
